import React from 'react';
import { Portal } from 'react-portal';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';
import { theme, toRem } from 'design';
import { ModalContent, ModalHeader, ModalOverlay, ModalText, ModalTitle } from '../Modal';
import LoaderSpin from './LoaderSpin';

const Container = styled(motion.div).attrs({
  'aria-hidden': 'true',
})`
  position: fixed;
  top: 0;
  left: 0;
  z-index: ${theme.zIndex.modal};
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: ${toRem(theme.layout.gutter.small)};
  background-color: transparent;

  @media (min-width: ${theme.breakpoint.med}px) {
    justify-content: center;
    padding: 0;
  }
`;

const Layout = styled.section`
  position: relative;
  z-index: ${theme.zIndex.modal + 2};
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: ${toRem(400)};
  height: auto;
  padding: 0 ${toRem(theme.layout.gutter.small)};
  border-radius: ${toRem(22)};
  background-color: ${theme.color.white};
  box-shadow: ${theme.shadow.modal};

  @media (min-width: ${theme.breakpoint.med}px) {
    height: ${toRem(380)};
    padding: 0 ${toRem(44)};
  }
`;

const StyledModalHeader = styled(ModalHeader)`
  padding-bottom: ${toRem(16.5)};
`;

const StyledModalContent = styled(ModalContent)`
  flex-grow: 0;
  padding-bottom: ${toRem(44)};
`;

const StyledLoaderSpin = styled(LoaderSpin).attrs({
  size: 55,
  stroke: 5.5,
  color: theme.color.secondary[300],
})`
  margin-top: ${toRem(12)};
  margin-bottom: ${toRem(27.5)};
`;

interface LoaderModalProps {
  isActive?: boolean;
  title?: string;
  message?: string;
}

const LoaderModal: React.FC<LoaderModalProps> = ({ isActive, title = 'Loading', message = 'One moment please...' }) => (
  <Portal>
    <AnimatePresence>
      {isActive && (
        <>
          <Container initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ ease: 'easeInOut', duration: 0.2 }}>
            <Layout>
              <StyledModalHeader>
                <StyledLoaderSpin />
                <ModalTitle>{title}</ModalTitle>
              </StyledModalHeader>

              <StyledModalContent>
                <ModalText>{message}</ModalText>
              </StyledModalContent>
            </Layout>

            <ModalOverlay />
          </Container>
        </>
      )}
    </AnimatePresence>
  </Portal>
);

export default LoaderModal;
